import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  advancement as advApi,
  ApiError,
  meetings as meetingsApi,
  RANK_LABELS,
  scouts as scoutsApi,
  type AvailableAdventure,
  type MeetingDetail as MeetingDetailT,
  type ScoutRow,
} from '../api';
import { Attribution } from '../components/Attribution';
import { useUnits } from '../units';

export function MeetingDetail() {
  const { unitId = '', meetingId = '' } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('DEN_LEADER');

  const [meeting, setMeeting] = useState<MeetingDetailT | null>(null);
  const [available, setAvailable] = useState<AvailableAdventure[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    meetingsApi
      .get(unitId, meetingId)
      .then(setMeeting)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Not found'));
    meetingsApi
      .availableAdventures(unitId, meetingId)
      .then((r) => setAvailable(r.adventures))
      .catch(() => setAvailable([]));
  }, [unitId, meetingId]);

  useEffect(() => {
    if (mayView) load();
  }, [mayView, load]);

  if (!mayView) return <Navigate to="/" replace />;
  if (error) return <div className="panel"><p className="error">{error}</p></div>;
  if (!meeting) return <p className="muted">Loading…</p>;

  async function toggle(sourceId: string, checked: boolean) {
    setMeeting(await meetingsApi.setAdventure(unitId, meetingId, sourceId, checked));
    const r = await meetingsApi.availableAdventures(unitId, meetingId);
    setAvailable(r.adventures);
  }

  return (
    <div className="panel">
      <p className="muted small">
        <Link to={`/units/${unitId}/meetings`}>← Meetings</Link> · {meeting.denName}
        {meeting.denRank ? ` (${RANK_LABELS[meeting.denRank]})` : ''}
      </p>

      <MeetingHeader
        meeting={meeting}
        onSaved={setMeeting}
        onDeleted={() => navigate(`/units/${unitId}/meetings`)}
        unitId={unitId}
      />

      <h2>Adventures covered</h2>
      {available && (
        <div className="adv-checklist">
          {available.map((a) => (
            <label key={a.sourceId} className="adv-check">
              <input
                type="checkbox"
                checked={a.checked}
                onChange={(e) => toggle(a.sourceId, e.target.checked)}
              />
              <span>{a.name}</span>
              {a.officialUrl && (
                <a href={a.officialUrl} target="_blank" rel="noreferrer" className="muted small">
                  guide ↗
                </a>
              )}
              {a.supplyCount > 0 && <span className="muted small">{a.supplyCount} supplies</span>}
            </label>
          ))}
          {available.length === 0 && (
            <p className="muted">
              No adventures for this den’s rank in the catalog yet. Set the den’s rank on the Dens
              page, or run a catalog sync.
            </p>
          )}
        </div>
      )}

      {meeting.adventures.some((a) => a.requirements || a.hasNoPublishedRequirements) && (
        <>
          <h3>Requirements covered</h3>
          <p className="muted small">
            Tick the requirements this meeting actually worked on — those are what “Record
            advancement” check-marks for the Scouts who attended.
          </p>
          {meeting.adventures.map((a) => (
            <RequirementsCovered
              key={a.sourceId}
              unitId={unitId}
              meetingId={meeting.id}
              adventure={a}
              onChanged={setMeeting}
            />
          ))}
          <Attribution />
        </>
      )}

      <h2>Supply list</h2>
      <SupplyList meeting={meeting} unitId={unitId} onChanged={setMeeting} />

      {meeting.adventures.length > 0 && (
        <>
          <h2>Record advancement</h2>
          {meeting.advancementRecorded.requirements > 0 && (
            <p className="muted small">
              So far: {meeting.advancementRecorded.requirements} requirement
              {meeting.advancementRecorded.requirements === 1 ? '' : 's'} recorded from this meeting
              across {meeting.advancementRecorded.scouts} Scout
              {meeting.advancementRecorded.scouts === 1 ? '' : 's'}.
            </p>
          )}
          <RecordAdvancement unitId={unitId} meeting={meeting} />
        </>
      )}
    </div>
  );
}

function RequirementsCovered({
  unitId,
  meetingId,
  adventure: a,
  onChanged,
}: {
  unitId: string;
  meetingId: string;
  adventure: MeetingDetailT['adventures'][number];
  onChanged: (m: MeetingDetailT) => void;
}) {
  const [busy, setBusy] = useState(false);
  const allNumbers = (a.requirements ?? []).map((r) => r.number);
  const covered = a.coveredRequirements; // null = all
  const isCovered = (n: string) => covered === null || covered.includes(n);

  async function toggle(n: string) {
    const current = covered ?? allNumbers;
    const next = current.includes(n) ? current.filter((x) => x !== n) : [...current, n];
    setBusy(true);
    try {
      onChanged(
        await meetingsApi.setAdventureRequirements(
          unitId,
          meetingId,
          a.sourceId,
          next.length === allNumbers.length ? null : next,
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="req-block">
      <strong>{a.name}</strong>
      {a.isShootingSports ? (
        <p className="muted small">Completed only at approved events — no checklist.</p>
      ) : (
        a.hasNoPublishedRequirements && (
          <p className="muted small">No requirements in the catalog for this adventure.</p>
        )
      )}
      {a.requirements && (
        <ol className="reqs">
          {a.requirements.map((r) => (
            <li key={r.number}>
              <label className="adv-check">
                <input
                  type="checkbox"
                  checked={isCovered(r.number)}
                  disabled={busy}
                  onChange={() => toggle(r.number)}
                />
                <span className={isCovered(r.number) ? '' : 'muted'}>{r.text}</span>
              </label>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function RecordAdvancement({
  unitId,
  meeting,
}: {
  unitId: string;
  meeting: MeetingDetailT;
}) {
  const [roster, setRoster] = useState<ScoutRow[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A pack-wide meeting (denId null) records advancement against the whole roster —
    // omitting the filter, not passing null, is what "no den" means to this endpoint.
    scoutsApi
      .list(unitId, { denId: meeting.denId ?? undefined })
      .then((r) => setRoster(r.scouts))
      .catch(() => setRoster([]));
  }, [unitId, meeting.denId]);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function apply() {
    setBusy(true);
    setResult(null);
    try {
      const r = await advApi.applyMeeting(unitId, meeting.id, [...picked]);
      setResult(`Marked ${r.added} requirement${r.added === 1 ? '' : 's'} across ${r.scouts} Scout${r.scouts === 1 ? '' : 's'}.`);
      setPicked(new Set());
    } catch (e) {
      setResult(e instanceof ApiError ? e.message : 'Could not apply');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="muted small">
        Check-marks the requirements covered above (all of an adventure’s requirements unless you
        narrowed it) for the Scouts who attended — skipping anything already complete.
      </p>
      <div className="adv-checklist">
        {roster?.map((s) => (
          <label key={s.id} className="adv-check">
            <input type="checkbox" checked={picked.has(s.id)} onChange={() => toggle(s.id)} />
            <span>
              {s.lastName}, {s.firstName}
            </span>
          </label>
        ))}
        {roster?.length === 0 && <p className="muted">No Scouts in this den yet.</p>}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <button className="primary" disabled={busy || picked.size === 0} onClick={apply}>
          Apply to {picked.size} selected
        </button>
        {result && <span className="muted small">{result}</span>}
      </div>
    </div>
  );
}

function MeetingHeader({
  meeting,
  unitId,
  onSaved,
  onDeleted,
}: {
  meeting: MeetingDetailT;
  unitId: string;
  onSaved: (m: MeetingDetailT) => void;
  onDeleted: () => void;
}) {
  const [date, setDate] = useState(meeting.date);
  const [startTime, setStartTime] = useState(meeting.startTime ?? '');
  const [title, setTitle] = useState(meeting.title ?? '');
  const [notes, setNotes] = useState(meeting.notes ?? '');
  const [busy, setBusy] = useState(false);

  const dirty =
    date !== meeting.date ||
    startTime !== (meeting.startTime ?? '') ||
    title !== (meeting.title ?? '') ||
    notes !== (meeting.notes ?? '');

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved(
        await meetingsApi.update(unitId, meeting.id, {
          date,
          startTime: startTime || null,
          title: title || null,
          notes: notes || null,
        }),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="meeting-header" onSubmit={save}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
        <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <button type="submit" className="primary" disabled={busy || !dirty}>
          Save
        </button>
        <button
          type="button"
          onClick={async () => {
            onSaved(
              await meetingsApi.update(unitId, meeting.id, { finalized: !meeting.finalized }),
            );
          }}
        >
          {meeting.finalized ? 'Un-finalize' : 'Finalize'}
        </button>
        <button
          type="button"
          onClick={async () => {
            if (confirm('Delete this meeting?')) {
              await meetingsApi.remove(unitId, meeting.id);
              onDeleted();
            }
          }}
        >
          Delete
        </button>
      </div>
      <textarea
        rows={2}
        placeholder="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
    </form>
  );
}

function SupplyList({
  meeting,
  unitId,
  onChanged,
}: {
  meeting: MeetingDetailT;
  unitId: string;
  onChanged: (m: MeetingDetailT) => void;
}) {
  const [item, setItem] = useState('');
  const [qty, setQty] = useState('');

  return (
    <>
      <table className="grid">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>From</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {meeting.supplies.map((s) => (
            <SupplyRow key={s.id} meeting={meeting} supply={s} unitId={unitId} onChanged={onChanged} />
          ))}
          {meeting.supplies.length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                Check adventures above to pull in their supplies, or add items by hand.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <form
        className="add-member"
        onSubmit={async (e) => {
          e.preventDefault();
          onChanged(await meetingsApi.addSupply(unitId, meeting.id, { item, quantity: qty || undefined }));
          setItem('');
          setQty('');
        }}
      >
        <input placeholder="Add an item" value={item} onChange={(e) => setItem(e.target.value)} required />
        <input placeholder="Qty" value={qty} onChange={(e) => setQty(e.target.value)} style={{ minWidth: 90 }} />
        <button type="submit" className="primary">
          Add
        </button>
      </form>
    </>
  );
}

function SupplyRow({
  meeting,
  supply,
  unitId,
  onChanged,
}: {
  meeting: MeetingDetailT;
  supply: MeetingDetailT['supplies'][number];
  unitId: string;
  onChanged: (m: MeetingDetailT) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [item, setItem] = useState(supply.item);
  const [qty, setQty] = useState(supply.quantity ?? '');

  return (
    <tr>
      <td>
        {editing ? (
          <input value={item} onChange={(e) => setItem(e.target.value)} />
        ) : (
          <>
            {supply.item}
            {supply.editedManually && supply.source === 'auto' && (
              <span className="badge" style={{ marginLeft: 6 }}>edited</span>
            )}
          </>
        )}
      </td>
      <td>
        {editing ? (
          <input value={qty} onChange={(e) => setQty(e.target.value)} style={{ minWidth: 80 }} />
        ) : (
          (supply.quantity ?? '—')
        )}
      </td>
      <td className="muted small">
        {supply.source === 'manual' ? 'added by hand' : supply.fromAdventures.length + ' adventure(s)'}
      </td>
      <td className="row-actions">
        {editing ? (
          <>
            <button
              className="primary"
              onClick={async () => {
                onChanged(
                  await meetingsApi.updateSupply(unitId, meeting.id, supply.id, {
                    item,
                    quantity: qty || null,
                  }),
                );
                setEditing(false);
              }}
            >
              Save
            </button>
            <button onClick={() => setEditing(false)}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)}>Edit</button>
            <button
              onClick={async () =>
                onChanged(await meetingsApi.deleteSupply(unitId, meeting.id, supply.id))
              }
            >
              Remove
            </button>
          </>
        )}
      </td>
    </tr>
  );
}
